import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC, MouseEvent, PropsWithChildren, ReactNode } from 'react';

import styles from './Chip.module.scss';

interface ChipProps {
  id: string;
  onClick?: (id: string, event: MouseEvent) => void;
  onDelete?: (id: string) => void;
  color?: 'attention' | 'highlight' | 'subtle';
  variant?: 'filled' | 'outlined';
  size?: 'small' | 'medium';
  'aria-label': string;
  children: ReactNode;
}

const Chip = ({
  ref: forwardedRef,
  id,
  children,
  onClick,
  onDelete,
  color = 'highlight',
  variant = 'filled',
  size = 'small',
  'aria-label': ariaLabel,
}: ChipProps & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  return (
    <div
      className={styles['chip']}
      data-variant={variant}
      data-color={color}
      data-size={size}
      data-deletable={!!onDelete}
      aria-label={ariaLabel}
      ref={forwardedRef}
    >
      {!!onClick && (
        <button className={styles['content-container']} onClick={(event) => onClick(id, event)} type="button">
          {children}
        </button>
      )}
      {!onClick && <div className={styles['content-container']}>{children}</div>}
      {onDelete && (
        <button
          className={styles['chip-delete-icon']}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(id);
          }}
          aria-label=""
          type="button"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </div>
  );
};

const ChipIcon: FC<PropsWithChildren<unknown>> = ({ children }) => (
  <span className={styles['chip-icon']}>{children}</span>
);

const ChipText: FC<PropsWithChildren<unknown>> = ({ children }) => (
  <span className={styles['chip-text']}>{children}</span>
);

Chip.displayName = 'Chip';
export default Chip;
export { ChipText, ChipIcon };
